import React, { useLayoutEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { UnlockIconStyled } from "./tm-components";
import { STATIC_PATH } from "../../constants/config";
import { updateTimelineTime } from "../../redux/actions/timelineUtils";
import { setSelectedItems } from "../../redux/actions/appUtils";

const UnlockIcon = ({ lockedObjects, zoomFactor = 1, workspaceStage }) => {
    const iconRef = useRef(null);
    const dispatch = useDispatch();

    // Function to rotate a point around a pivot by a given angle
    const getRotatedPoint = (pivot, point, angle) => {
        const rad = (angle * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        return {
            x: (point.x - pivot.x) * cos - (point.y - pivot.y) * sin + pivot.x,
            y: (point.x - pivot.x) * sin + (point.y - pivot.y) * cos + pivot.y,
        };
    };

    // Function to compute rotated bounding box
    const getRotatedBox = (topLeft, topRight, bottomLeft, bottomRight, pivot, angle) => {
        const rotatedPoints = [topLeft, topRight, bottomLeft, bottomRight].map(point =>
            getRotatedPoint(pivot, point, angle)
        );

        const xValues = rotatedPoints.map(({ x }) => x);
        const yValues = rotatedPoints.map(({ y }) => y);

        const minX = Math.min(...xValues);
        const maxX = Math.max(...xValues);
        const minY = Math.min(...yValues);
        const maxY = Math.max(...yValues);

        return {
            bounding_box: {
                top: minY,
                left: minX,
                width: maxX - minX,
                height: maxY - minY,
            },
            rotated_points: {
                top_left: rotatedPoints[0],
                top_right: rotatedPoints[1],
                bottom_left: rotatedPoints[2],
                bottom_right: rotatedPoints[3],
            },
        };
    };

    // Get rotated item dimensions based on zoom factor
    const getRotatedItem = (item) => {
        const x = item.get("x") * zoomFactor;
        const y = item.get("y") * zoomFactor;
        const width = item.get("width") * zoomFactor;
        const height = item.get("height") * zoomFactor;
        const angle = item.get("angle");

        return getRotatedBox(
            { x, y },
            { x: x + width, y },
            { x, y: y + height },
            { x: x + width, y: y + height },
            { x: x + width / 2, y: y + height / 2 },
            angle
        );
    };

    // Function to unlock item
    const handleUnlockItem = () => {
        if (!lockedObjects.first().get("isRestrict")) {
            const key = lockedObjects.keySeq().first();
            dispatch(updateTimelineTime({
                toUpdate: [{ container: "workspaceItems", id: key, toUpdate: { isLock: false } }],
            }));
            dispatch(setSelectedItems({ selectedItems: [] }));
        }
    };

    // Adjust icon position based on locked object and workspace visibility
    const handleIconPosition = () => {
        if (!(iconRef.current instanceof HTMLElement)) return;
        const workspaceEl = document.getElementById("workspace");
        if (!(workspaceEl instanceof HTMLElement)) return;

        const iconOffset = { x: -iconRef.current.offsetWidth / 2, y: -iconRef.current.offsetHeight / 2 };
        let iconPos = { x: 0, y: 0 };

        if (lockedObjects.size === 1) {
            const rotatedItem = getRotatedItem(lockedObjects.first());
            iconPos = rotatedItem.rotated_points.top_left;
        }

        iconRef.current.style.left = `${iconPos.x + iconOffset.x}px`;
        iconRef.current.style.top = `${iconPos.y + iconOffset.y}px`;
    };

    useLayoutEffect(() => {
        handleIconPosition();

        const workspaceEl = document.getElementById("workspace");
        if (workspaceEl) {
            workspaceEl.addEventListener("scroll", handleIconPosition);
        }
        return () => {
            if (workspaceEl) {
                workspaceEl.removeEventListener("scroll", handleIconPosition);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lockedObjects, zoomFactor, workspaceStage]);

    return (
        <UnlockIconStyled
            ref={iconRef}
            $hoverSrc={`${STATIC_PATH}lock.svg`}
            className="tool-bar"
            onClick={handleUnlockItem}>
            <img alt="lock" src={`${STATIC_PATH}lock.svg`} />
        </UnlockIconStyled>
    );
};

UnlockIcon.propTypes = {
    lockedObjects: PropTypes.object.isRequired,
    zoomFactor: PropTypes.number,
    workspaceStage: PropTypes.object.isRequired,
};

export default UnlockIcon;